import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Phone } from "lucide-react";
import { COMPANY } from "@/lib/site";
import { SERVICES } from "@/lib/services";

export default function UslugePage() {
  const { t } = useTranslation();
  return (
    <div data-testid="usluge-page">
      <Seo title={t("servicesPage.seoTitle")} path="/usluge" description={t("servicesPage.seoDesc")} />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> {t("servicesPage.kicker")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                {t("servicesPage.heroTitle1")}<br />
                <span className="text-ink/40">{t("servicesPage.heroTitle2")}</span><br />
                {t("servicesPage.heroTitle3")}
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              {t("servicesPage.heroLead")}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE CARDS — clickable */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const to = s.external || `/usluge/${s.slug}`;
            const title = t(`services.items.${s.slug}.title`);
            const intro = t(`services.items.${s.slug}.intro`);
            return (
              <Link
                key={s.slug}
                to={to}
                className="group border border-hair bg-white hover-lift relative overflow-hidden flex flex-col"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={s.image}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors" />
                  <div className={`absolute top-4 left-4 w-11 h-11 ${s.external ? "bg-[#D92D20]" : "bg-ink"} text-white flex items-center justify-center`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="absolute top-4 right-4 font-brand uppercase text-[10px] tracking-[0.2em] bg-white/90 px-2 py-1 text-ink">
                    0{i + 1}
                  </span>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="font-display text-xl font-semibold text-ink tracking-tight leading-snug">
                    {title}
                  </h3>
                  <p className="text-ink-soft text-[15px] leading-relaxed mt-2 flex-1">
                    {intro}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-ink text-sm font-medium opacity-75 group-hover:opacity-100 transition-opacity">
                    {t("common.learnMore")}
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-[#F4A8A0]">{t("servicesPage.ctaKicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-3 leading-tight">
              {t("servicesPage.ctaTitle")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right flex md:justify-end gap-3 flex-wrap">
            <Link
              to="/prijava"
              data-testid="usluge-cta"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              {t("common.sendInquiry")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/10 transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.75} />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
